import { Box, ButtonBase, GlobalStyles, IconButton, Paper, Typography, alpha } from '@mui/material';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import KeyboardCapslockIcon from '@mui/icons-material/KeyboardCapslock';
import KeyboardHideIcon from '@mui/icons-material/KeyboardHide';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useGetLojaAtualQuery } from '../api';
import { usaTecladoDeTela } from '../lib/canal';
import { proximoCampo } from '../lib/foco-formulario';
import { vigiarTecladoDoSistema } from '../lib/teclado-sistema';
import {
  ACENTUADAS,
  LAYOUTS,
  acaoDoAvanco,
  acaoPrincipal,
  aplicarTecla,
  escreverNoCampo,
  garantirFoco,
  layoutDoCampo,
  selecaoDe,
  teclaBloqueada,
  type CampoEditavel,
  type LayoutTeclado,
  type TeclaTeclado,
} from '../lib/teclado-tela';

type EstadoShift = 'off' | 'on' | 'caps';
type AcaoAvanco = ReturnType<typeof acaoDoAvanco>;

const ALTURA_MIN_TECLA = 64; // dedo, totem em pé, ângulo ruim — 48 do Material fica curto
/** Tablet deitado tem ~600px de altura: com a tecla cheia a faixa (331px) empurrava o diálogo para fora da tela. */
const ALTURA_TECLA_TELA_BAIXA = 52;
const TELA_BAIXA = '@media (max-height: 680px)';
const REPETIR_APOS_MS = 350;
const REPETIR_CADA_MS = 60;
/** Folga, DEPOIS de soltar o dedo, para o `click` ser despachado antes do layout mudar. */
const ATRASO_FECHAR_MS = 150;

/** Nome/label do campo focado, para o cabeçalho da faixa. */
function rotuloDoCampo(el: CampoEditavel): string {
  const id = el.getAttribute('id');
  // `CSS.escape` não existe em todo ambiente (jsdom, por exemplo) — o rótulo é
  // enfeite de cabeçalho, não pode derrubar o teclado.
  let label: Element | null = null;
  try {
    if (id) label = document.querySelector(`label[for="${CSS.escape(id)}"]`);
  } catch {
    label = null;
  }
  return label?.textContent?.trim() || el.getAttribute('aria-label') || el.getAttribute('placeholder') || '';
}

/** Começa em maiúscula: campo vazio ou logo após espaço/quebra de linha. */
function deveIniciarMaiuscula(el: CampoEditavel): boolean {
  const pos = el.selectionStart ?? el.value.length;
  const anterior = el.value.slice(0, pos).replace(/\s+$/, '');
  return anterior.length === 0;
}

/**
 * Teclado na tela dos aparelhos da loja (TOTEM, TOTEM_CHECKOUT e TABLET).
 *
 * Existe porque o totem roda em Windows e o teclado de toque do sistema não
 * abre: leitor de código de barras e pinpad se enumeram como teclado USB, então
 * o Windows entende que já existe teclado físico e nunca mostra o dele. Depender
 * daquele teclado deixa o cliente travado na tela de identificação.
 *
 * No tablet o motivo é outro: com o site em tela cheia, o teclado do Android traz
 * a barra de navegação de volta e o Chrome redimensiona a janela — a tela pisca
 * preto toda vez que um campo ganha foco (medido por adb em 13/10/2026).
 *
 * Desenho: um único listener de `focusin` no documento descobre o campo e o
 * layout pelo `inputMode`/`type` — nenhuma das telas precisa saber que o teclado
 * existe. Isso também cobre o foco programático, que é a regra no totem
 * (`checkout-final` foca o nome por ref, a busca foca no `onEntered` do Dialog).
 *
 * Só monta nesses aparelhos; nos demais canais (o celular do cliente) retorna
 * `null` sem instalar listener.
 *
 * Invariante que sustenta o posicionamento: **todo campo digitável do totem está
 * dentro de um `Dialog`**. A faixa compensa a altura encolhendo o
 * `.MuiDialog-container`, o que empurra o diálogo inteiro para cima e mantém o
 * botão do rodapé (CONTINUAR) à vista. Se algum dia existir campo fora de
 * Dialog, o ajuste é usar `var(--teclado-h, 0px)` no elemento fixo daquela tela.
 *
 * O teclado não mascara nada: injeta o caractere e deixa o `onChange` do campo
 * decidir, igual a um teclado físico. Se o campo rejeita o caractere, a tecla
 * simplesmente não faz efeito — é o comportamento correto, não um defeito.
 */
export function TecladoTela(): JSX.Element | null {
  const { data } = useGetLojaAtualQuery();
  const totem = !!data && usaTecladoDeTela(data.identificador);

  const [campo, setCampo] = useState<CampoEditavel | null>(null);
  const [layoutCampo, setLayoutCampo] = useState<LayoutTeclado | null>(null);
  const [layoutManual, setLayoutManual] = useState<LayoutTeclado | null>(null);
  const [shift, setShift] = useState<EstadoShift>('off');
  const [gaveta, setGaveta] = useState(false);
  const [altura, setAltura] = useState(0);

  const campoRef = useRef<CampoEditavel | null>(null);
  const refFaixa = useRef<HTMLDivElement | null>(null);
  const timers = useRef<{ atraso?: number; repeticao?: number; fechamento?: number }>({});
  // Conjunto de ponteiros, não booleano: totem é superfície grande e a palma
  // apoiada conta como um segundo ponteiro. Com booleano, o primeiro dedo a sair
  // liberaria o fechamento com o outro ainda na tela.
  const ponteiros = useRef(new Set<number>());
  const fecharPendente = useRef(false);

  // Sincroniza por efeito, não no corpo do render: escrever ref durante o render
  // deixa a comparação de idempotência olhando para o commit anterior.
  useEffect(() => {
    campoRef.current = campo;
  }, [campo]);

  const layout = layoutManual ?? layoutCampo;
  const aberto = !!campo && !!layout;

  const fechar = useCallback(() => {
    setCampo(null);
    setLayoutCampo(null);
    setLayoutManual(null);
    setGaveta(false);
    setShift('off');
  }, []);

  /*
   * Enquanto este teclado está no ar, o do sistema fica calado. No Windows ele
   * nunca abria e a chamada é inócua; no Android ele abre em cima do nosso,
   * tapando o campo e empurrando o layout. Fica aqui, e não nas telas, porque
   * é o mesmo motivo de este componente existir — e assim nenhuma tela precisa
   * saber do assunto.
   */
  useEffect(() => {
    if (!totem) return;
    return vigiarTecladoDoSistema();
  }, [totem]);

  // --- Descobre o campo focado (toque ou foco programático) ---
  useEffect(() => {
    if (!totem) return;

    function aoFocar(e: FocusEvent): void {
      const alvo = e.target as Element | null;
      const novoLayout = layoutDoCampo(alvo);
      if (!novoLayout) {
        // Foco foi para algo que não é campo — tipicamente o próprio botão que
        // o cliente está tocando. NUNCA fechar direto aqui: o `focusin` do
        // botão chega no pointerdown, e fechar devolve a altura ao diálogo,
        // arrastando o botão para longe do dedo antes do `pointerup`. Vai pela
        // mesma fila do focusout, que só corre depois que o dedo sai.
        agendarFechamento();
        return;
      }
      // Voltou o foco para um campo: cancela o fechamento que estava agendado.
      window.clearTimeout(timers.current.fechamento);
      fecharPendente.current = false;
      const el = alvo as CampoEditavel;
      // Idempotente: re-render que remonta o input dispara focusin de novo e
      // não pode zerar shift nem re-rolar a tela.
      if (campoRef.current === el) {
        setLayoutCampo(novoLayout);
        return;
      }
      setCampo(el);
      setLayoutCampo(novoLayout);
      setLayoutManual(null);
      setGaveta(false);
      setShift(novoLayout === 'ABC' && deveIniciarMaiuscula(el) ? 'on' : 'off');
    }

    /**
     * Fechar devolve a altura ao diálogo, e o botão do rodapé (CONTINUAR) DESCE
     * uns 400px. Se isso acontecer enquanto o dedo ainda está na tela, o
     * `pointerup` cai onde o botão não está mais e o navegador dispara o `click`
     * no ancestral comum — o `onClick` do botão nunca roda e o passo não avança.
     *
     * Por isso o fechamento nunca acontece com o dedo encostado: fica pendente
     * até soltar, e só então o prazo curto corre (o `click` é despachado logo
     * depois do `pointerup`).
     */
    function agendarFechamento(): void {
      window.clearTimeout(timers.current.fechamento);
      if (ponteiros.current.size > 0) {
        fecharPendente.current = true;
        return;
      }
      fecharPendente.current = false;
      timers.current.fechamento = window.setTimeout(() => {
        if (!layoutDoCampo(document.activeElement)) fechar();
      }, ATRASO_FECHAR_MS);
    }

    function aoDesfocar(): void {
      agendarFechamento();
    }

    function aoEncostar(e: PointerEvent): void {
      ponteiros.current.add(e.pointerId);
    }

    function aoSoltarDedo(e: PointerEvent): void {
      ponteiros.current.delete(e.pointerId);
      if (ponteiros.current.size === 0 && fecharPendente.current) agendarFechamento();
    }

    /**
     * Vigia de campo removido. Quando o React desmonta o input focado, Chrome e
     * Edge NÃO disparam blur/focusout (só o Firefox) — o foco vai para o body em
     * silêncio. Sem isto, a faixa fica presa na tela apontando para um campo que
     * não existe mais, e num totem isso trava a operação.
     */
    const vigia = window.setInterval(() => {
      const el = campoRef.current;
      if (el && !el.isConnected) fechar();
    }, 1000);

    document.addEventListener('focusin', aoFocar);
    document.addEventListener('focusout', aoDesfocar);
    // Captura: precisamos saber do toque antes de qualquer handler da aplicação.
    document.addEventListener('pointerdown', aoEncostar, true);
    document.addEventListener('pointerup', aoSoltarDedo, true);
    document.addEventListener('pointercancel', aoSoltarDedo, true);
    return () => {
      document.removeEventListener('focusin', aoFocar);
      document.removeEventListener('focusout', aoDesfocar);
      document.removeEventListener('pointerdown', aoEncostar, true);
      document.removeEventListener('pointerup', aoSoltarDedo, true);
      document.removeEventListener('pointercancel', aoSoltarDedo, true);
      window.clearInterval(vigia);
    };
  }, [totem, fechar]);

  // --- Altura real da faixa vira --teclado-h (nada de vh chutado) ---
  useLayoutEffect(() => {
    const el = refFaixa.current;
    if (!el) {
      setAltura(0);
      return;
    }
    const observador = new ResizeObserver(() => setAltura(el.offsetHeight));
    observador.observe(el);
    setAltura(el.offsetHeight);
    return () => observador.disconnect();
  }, [aberto]);

  // --- Traz o campo para o meio do que sobrou de tela ---
  useEffect(() => {
    if (!campo || !altura) return;
    // Dois rAF: o segundo é agendado dentro do primeiro, então precisa do
    // próprio id guardado — cancelar só o externo deixaria o interno rodar
    // depois da troca de campo ou da desmontagem.
    let interno = 0;
    const externo = requestAnimationFrame(() => {
      interno = requestAnimationFrame(() => {
        if (campo.isConnected) campo.scrollIntoView({ block: 'center' });
      });
    });
    return () => {
      cancelAnimationFrame(externo);
      cancelAnimationFrame(interno);
    };
  }, [campo, layout, altura]);

  const pararRepeticao = useCallback(() => {
    // Só os timers da repetição: zerar o objeto inteiro perderia o id do
    // fechamento agendado e ele dispararia sem ninguém para cancelar.
    window.clearTimeout(timers.current.atraso);
    window.clearInterval(timers.current.repeticao);
    timers.current.atraso = undefined;
    timers.current.repeticao = undefined;
  }, []);

  useEffect(() => {
    const pendentes = timers.current;
    return () => {
      window.clearTimeout(pendentes.atraso);
      window.clearInterval(pendentes.repeticao);
      window.clearTimeout(pendentes.fechamento);
    };
  }, []);

  const apagar = useCallback(() => {
    const el = campoRef.current;
    if (!el || !el.isConnected) return;
    garantirFoco(el);
    const valor = el.value;
    const { ini, fim } = selecaoDe(el);
    const r = aplicarTecla(valor, ini, fim, { tipo: 'backspace' }, el.maxLength);
    if (r.valor === valor) return;
    escreverNoCampo(el, r.valor, r.caret);
  }, []);

  const acionar = useCallback(
    (tecla: TeclaTeclado) => {
      const el = campoRef.current;
      if (!el || !layout) return;
      if (!el.isConnected) {
        fechar();
        return;
      }
      garantirFoco(el);

      switch (tecla.tipo) {
        case 'shift':
          setShift((s) => (s === 'off' ? 'on' : s === 'on' ? 'caps' : 'off'));
          return;
        case 'acentos':
          setGaveta((g) => !g);
          return;
        case 'numerico':
          setLayoutManual('NUMERICO');
          return;
        case 'abc':
          setLayoutManual('ABC');
          return;
        case 'backspace':
          // Limpa antes de armar: dois ponteiros na mesma tecla sobrescreveriam
          // o slot e deixariam um setInterval órfão apagando sozinho.
          pararRepeticao();
          apagar();
          timers.current.atraso = window.setTimeout(() => {
            timers.current.repeticao = window.setInterval(apagar, REPETIR_CADA_MS);
          }, REPETIR_APOS_MS);
          return;
        case 'ok': {
          // Nunca sintetizamos Enter: em ConfigTotem o Enter confirma o PIN.
          switch (acaoDoAvanco(el)) {
            case 'linha': {
              const valor = el.value;
              const { ini, fim } = selecaoDe(el);
              const r = aplicarTecla(valor, ini, fim, { tipo: 'char', valor: '\n' }, el.maxLength);
              escreverNoCampo(el, r.valor, r.caret);
              return;
            }
            case 'proximo':
              proximoCampo(el)?.focus();
              return;
            case 'avancar': {
              // Último campo: aciona o CONTINUAR do rodapé. Se ainda estiver
              // desabilitado (falta preencher), só sai do teclado para o cliente
              // ver a tela — apertar e não acontecer nada seria pior.
              const botao = acaoPrincipal(el);
              if (botao && !botao.disabled) {
                botao.click();
                return;
              }
              el.blur();
              fechar();
              return;
            }
            default:
              el.blur();
              fechar();
              return;
          }
        }
        case 'char': {
          const valor = el.value;
          const { ini, fim } = selecaoDe(el);
          const char = layout === 'ABC' && shift !== 'off' ? tecla.valor.toUpperCase() : tecla.valor;
          if (teclaBloqueada(valor, char, layout)) return;
          const r = aplicarTecla(valor, ini, fim, { tipo: 'char', valor: char }, el.maxLength);
          escreverNoCampo(el, r.valor, r.caret);
          if (shift === 'on') setShift('off');
          if (gaveta) setGaveta(false);
          return;
        }
      }
    },
    [apagar, fechar, gaveta, layout, shift],
  );

  const linhas = useMemo(() => {
    if (!layout) return [];
    const base = LAYOUTS[layout];

    if (layout === 'ABC') {
      if (!gaveta) return base;
      const acentos: TeclaTeclado[] = ACENTUADAS.map((valor) => ({ tipo: 'char', valor }));
      return [acentos, ...base.slice(1)];
    }

    // Só quem veio do ABC pela tecla "123" ganha a volta. O CPF e o PIN são
    // numéricos por natureza — ali um "ABC" só serviria para digitar letra que
    // o campo vai recusar.
    if (layoutManual === 'NUMERICO') {
      const ultima = base[base.length - 1] ?? [];
      return [...base.slice(0, -1), [{ tipo: 'abc' } as TeclaTeclado, ...ultima]];
    }
    return base;
  }, [layout, layoutManual, gaveta]);

  if (!totem) return null;

  const acaoOk: AcaoAvanco = campo ? acaoDoAvanco(campo) : 'fechar';
  const rotulo = campo ? rotuloDoCampo(campo) : '';

  return (
    <>
      <GlobalStyles
        styles={{
          ':root': { '--teclado-h': `${altura}px` },
          // Reserva a altura da faixa DENTRO do container do Dialog. É padding e
          // não height de propósito: o MUI já declara `height: 100%` nessa mesma
          // classe, com a mesma especificidade e inserido depois — sobrescrever
          // a altura perderia a disputa e o rodapé (CONTINUAR) ficaria embaixo
          // do teclado. `box-sizing` explícito para não depender do CssBaseline.
          '.MuiDialog-container': {
            boxSizing: 'border-box',
            paddingBottom: 'var(--teclado-h, 0px)',
          },
          // O kiosk põe user-select:none no body; sem isto o cliente não
          // consegue posicionar o cursor no meio do texto.
          'input, textarea': { userSelect: 'text', WebkitUserSelect: 'text' },
        }}
      />

      {aberto && (
        <Paper
          ref={refFaixa}
          variant="outlined"
          role="group"
          aria-label="Teclado na tela"
          sx={(t) => ({
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: t.zIndex.modal + 50,
            p: 1,
            borderRadius: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'personal.customArea',
            boxShadow: 'none',
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flexGrow: 1 }}>
              {rotulo}
            </Typography>
            <IconButton size="small" aria-label="Fechar teclado" onPointerDown={(e) => { e.preventDefault(); campo?.blur(); fechar(); }}>
              <KeyboardHideIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              maxWidth: layout === 'ABC' ? 'none' : 420,
              mx: 'auto',
            }}
          >
            {linhas.map((linhaTeclas, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                {linhaTeclas.map((tecla, j) => (
                  <Tecla
                    key={j}
                    tecla={tecla}
                    shift={shift}
                    gaveta={gaveta}
                    acaoOk={acaoOk}
                    aoAcionar={acionar}
                    aoSoltar={pararRepeticao}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </>
  );
}

/** Rótulo da tecla de avanço — ela diz o que vai fazer, em vez de um "OK" mudo. */
const ROTULO_AVANCO: Record<AcaoAvanco, React.ReactNode> = {
  linha: <KeyboardReturnIcon />,
  proximo: 'Próximo',
  avancar: 'Continuar',
  fechar: 'OK',
};

const ARIA_AVANCO: Record<AcaoAvanco, string> = {
  linha: 'nova linha',
  proximo: 'próximo campo',
  avancar: 'continuar',
  fechar: 'fechar teclado',
};

function rotuloDaTecla(tecla: TeclaTeclado, shift: EstadoShift, acaoOk: AcaoAvanco): React.ReactNode {
  switch (tecla.tipo) {
    case 'char':
      if (tecla.valor === ' ') return 'espaço';
      return shift !== 'off' ? tecla.valor.toUpperCase() : tecla.valor;
    case 'shift':
      return <KeyboardCapslockIcon />;
    case 'backspace':
      return <BackspaceOutlinedIcon />;
    case 'acentos':
      return 'ÁÉÍ';
    case 'numerico':
      return '123';
    case 'abc':
      return 'ABC';
    case 'ok':
      return ROTULO_AVANCO[acaoOk];
  }
}

function ariaDaTecla(tecla: TeclaTeclado, acaoOk: AcaoAvanco): string {
  switch (tecla.tipo) {
    case 'char':
      if (tecla.valor === ' ') return 'espaço';
      if (tecla.valor === ',') return 'vírgula';
      if (tecla.valor === '.') return 'ponto';
      return tecla.valor;
    case 'shift':
      return 'maiúsculas';
    case 'backspace':
      return 'apagar';
    case 'acentos':
      return 'acentos';
    case 'numerico':
      return 'teclado numérico';
    case 'abc':
      return 'teclado de letras';
    case 'ok':
      return ARIA_AVANCO[acaoOk];
  }
}

/**
 * Tecla. Aciona no `pointerdown` com `preventDefault` — é o que impede o campo
 * de perder o foco (e, de quebra, o `enforceFocus` do Modal de puxar o foco de
 * volta). Sem `onClick`, senão a tecla dispara duas vezes.
 */
function Tecla({
  tecla,
  shift,
  gaveta,
  acaoOk,
  aoAcionar,
  aoSoltar,
}: {
  tecla: TeclaTeclado;
  shift: EstadoShift;
  gaveta: boolean;
  acaoOk: AcaoAvanco;
  aoAcionar: (t: TeclaTeclado) => void;
  aoSoltar: () => void;
}): JSX.Element {
  const especial = tecla.tipo !== 'char';
  const ligada = (tecla.tipo === 'shift' && shift !== 'off') || (tecla.tipo === 'acentos' && gaveta);
  const rotulo = rotuloDaTecla(tecla, shift, acaoOk);
  // "Próximo"/"Continuar" não cabem no corpo de 22px das teclas de letra.
  const textoLongo = typeof rotulo === 'string' && rotulo.length > 3;

  return (
    <ButtonBase
      tabIndex={-1}
      aria-label={ariaDaTecla(tecla, acaoOk)}
      onPointerDown={(e) => {
        e.preventDefault();
        aoAcionar(tecla);
      }}
      onPointerUp={aoSoltar}
      onPointerCancel={aoSoltar}
      onPointerLeave={aoSoltar}
      onContextMenu={(e) => e.preventDefault()}
      sx={(t) => ({
        flex: tecla.largura ?? 1,
        minHeight: ALTURA_MIN_TECLA,
        [TELA_BAIXA]: { minHeight: ALTURA_TECLA_TELA_BAIXA },
        borderRadius: 1,
        fontSize: textoLongo ? 16 : 22,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        touchAction: 'manipulation',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        color: 'text.primary',
        bgcolor: ligada
          ? alpha(t.palette.primary.main, 0.24)
          : especial
            ? alpha(t.palette.primary.main, 0.12)
            : 'background.paper',
        border: 1,
        borderColor: 'divider',
        transition: 'transform 60ms, background-color 60ms',
        '&:active': { bgcolor: 'personal.hoverList', transform: 'scale(0.97)' },
      })}
    >
      {rotulo}
    </ButtonBase>
  );
}
